import React, { useState, useEffect, useRef, useCallback } from 'react';
import { getBookmarksConfigValueId } from '@/components/utils/CorgeeHelper';
import { useSocketConnectionId } from '@/contexts/SocketContext';
import { dashboardGroupsService } from './service';
import { IconPicker } from './IconPicker';
import CustomFields from '../CustomFields/CustomFields';
import { getVisibleLayers } from '../Globals/ArrayUtil';
import { apiService } from '@/services/api';
import './DashboardGroups.css';
import '../Globals/ComponentHelpers.css';
import '../ConversationItem/ConversationViewTitle.css';

/**
 * Filters menu items based on configValueId business rules:
 * - configValueId 1 (Dashboards): return all items (no filter)
 * - configValueId 148 (Bookmarks): only type 5 (legacy hardcoded behavior)
 * - Any other configValueId (e.g. 9, 15): return only the item matching that configValueId
 * - If no match found, return all items as fallback
 */
const EXCLUDED_NAMES_FOR_PROJECT = ['StreamInformation', 'TaskToBeInvoiced', 'Users', 'Bookmarks'];

function filterMenuByConfigValue(
  items: any[],
  configValueId: number,
  projectId?: number | null,
): any[]
{
  if (!items || items.length === 0) return items;

  let result = items;

  // When inside a project, exclude non-project types
  if (projectId && projectId > 0)
  {
    result = result.filter((item: any) => !EXCLUDED_NAMES_FOR_PROJECT.includes(item.Name));
  }

  // No further filter for Dashboards (1)
  if (configValueId === 1) return result;

  // Bookmarks → legacy hardcodes only type 5
  const bookmarksId = getBookmarksConfigValueId();
  const targetId = configValueId === bookmarksId ? 5 : configValueId;
  const filtered = result.filter((item: any) =>
  {
    const id = item.ConfigValueId || item.ConfigValueID;
    return id === targetId;
  });

  // Fallback: if no match after configValue filter, return project-filtered list
  return filtered.length > 0 ? filtered : result;
}

interface AddInlineTaskProps {
  groupId: number; // The column ID
  dashboardId: number;
  configValueId: number; // Default config value ID for the dashboard/group
  projectId?: number;
  // Name of the project the hosting view is scoped to (ProjectDetails); shown
  // in the new card's Project detail without an extra fetch
  projectName?: string;
  onAdd: (taskData: any) => void;
  onCancel: () => void;
  api: any;
  config: any;
  userData: any;
}

export const AddInlineTask: React.FC<AddInlineTaskProps> = ({
  groupId,
  dashboardId,
  configValueId: initialConfigValueId,
  projectId = null,
  projectName = '',
  onAdd,
  onCancel,
  api,
  config: _config,
  userData: _userData,
}) =>
{
  const socketConnectionId = useSocketConnectionId();
  const [taskName, setTaskName] = useState('');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [menuItems, setMenuItems] = useState<any[]>([]);
  const [selectedItemType, setSelectedItemType] = useState<any>(null);
  const [preloadedLayers, setPreloadedLayers] = useState<any[]>([]);
  const [selectedLayer, setSelectedLayer] = useState<any>(null);
  const [selectedLayers, setSelectedLayers] = useState<any[]>([]);
  const [selectedSecurityGroups, setSelectedSecurityGroups] = useState<any[]>([]);
  const [showIconPicker, setShowIconPicker] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [emptyCustomFields, setEmptyCustomFields] = useState<any[]>([]);
  const [customFieldsReady, setCustomFieldsReady] = useState(false);
  // Key to force remount of CustomFields when item type changes
  const [customFieldsKey, setCustomFieldsKey] = useState(0);

  // State to track the selected project ID, initializing with prop and allowing updates from CustomFields
  const [currentProjectId, setCurrentProjectId] = useState<number | null>(projectId ?? null);

  // Inside a project dashboard the project is fixed: the Project selector is
  // hidden and any inbound project change is ignored, so a card can never be
  // created against a different project.
  const isProjectScoped = (projectId ?? 0) > 0;
  const [currentProjectName, setCurrentProjectName] = useState<string>('');
  const inputRef = useRef<HTMLInputElement>(null);
  const prevConfigValueIdRef = useRef<number | null>(null);
  const prevCustomFieldProjectIdRef = useRef<number | null | undefined>(undefined);
  // True while the initial fetchData is in flight — CF effect must not duplicate that fetch.
  const fetchDataRunningRef = useRef(false);

  // Sync if props change after mount. Inside a project view the hosting page
  // provides both id and name, so the Project detail shows the project the user
  // is standing in; outside a project it stays unset until one is picked.
  useEffect(() =>
  {
    setCurrentProjectId(projectId ?? null);
    setCurrentProjectName(projectId && projectId > 0 ? projectName : '');
  }, [projectId, projectName]);

  // Fetch empty custom fields when showing details or when item type changes
  useEffect(() =>
  {
    if (!selectedItemType) return;
    // Skip if fetchData is already fetching CF data for this same type/project.
    if (fetchDataRunningRef.current) return;

    const cfgId = selectedItemType?.ConfigValueId ?? selectedItemType?.ConfigValueID;
    const itemTypeChanged = prevConfigValueIdRef.current !== cfgId;
    prevConfigValueIdRef.current = cfgId;

    const projectChanged = prevCustomFieldProjectIdRef.current !== currentProjectId;
    prevCustomFieldProjectIdRef.current = currentProjectId;

    if (!itemTypeChanged && !projectChanged && customFieldsReady) return;

    setCustomFieldsReady(false);
    setEmptyCustomFields([]);

    const fetchEmptyFields = async () =>
    {
      try
      {
        const [fields, layerData] = await Promise.all([
          dashboardGroupsService.getEmptyCustomFields(cfgId, currentProjectId || undefined),
          dashboardGroupsService
            .getLayersByConfigValue(cfgId, currentProjectId || undefined)
            .catch(() => []),
        ]);
        const result = Array.isArray(fields) ? fields : ((fields as any)?.result ?? []);
        const emptyFields = Array.isArray(result) ? result : [];
        setEmptyCustomFields(emptyFields);

        setPreloadedLayers(
          getVisibleLayers(
            (layerData || []).map((l: any) => ({
              LayerId: l.LayerId,
              Name: l.Name || l.LayerName || '',
              LayerName: l.Name || l.LayerName || '',
              Color: l.Color || l.LayerColor || '',
              LayerColor: l.Color || l.LayerColor || '',
              TextColor: l.TextColor || false,
              IsDefault: l.IsDefault || false,
              isConfigLayer: true,
              PriorityInConfigValue: l.PriorityInConfigValue || 0,
            })),
          ),
        );
      }
      catch (error)
      {
        console.error('Error fetching empty custom fields:', error);
        setEmptyCustomFields([]);
        setPreloadedLayers([]);
      }
      finally
      {
        setCustomFieldsReady(true);
      }
    };

    fetchEmptyFields();
  }, [showDetails, selectedItemType, customFieldsKey, currentProjectId]);

  // Ref to store custom fields data from the CustomFields component
  const customFieldsDataRef = useRef<any[]>([]);
  const conversationRef = useRef<any>(null);

  // Initial fetch
  useEffect(() =>
  {
    let cancelled = false;

    const fetchData = async () =>
    {
      fetchDataRunningRef.current = true;
      setLoading(true);
      try
      {
        // 1. Fetch menu data first to determine the correct configValueId
        const menuData = await dashboardGroupsService
          .getMenuByConfigValue(projectId ?? null)
          .catch(() => []);

        // 1.5 Filter menu items based on configValueId business rules
        const filteredMenuData = filterMenuByConfigValue(
          menuData || [],
          initialConfigValueId,
          projectId,
        );
        setMenuItems(filteredMenuData);

        // 2. Determine the correct configIDRoute from menu data
        let configIDRoute = initialConfigValueId;
        let defaultItem = null;

        if (filteredMenuData && filteredMenuData.length > 0)
        {
          const getConfigItemDefault = (items: any[]) =>
          {
            let configValueId = 0;
            for (let x = 0; x < items.length; x++)
            {
              const id = items[x].ConfigValueId || items[x].ConfigValueID;
              if (id !== 1 && id !== 9 && id !== 15)
              {
                configValueId = id;
                break;
              }
            }
            return configValueId;
          };

          const isFromProject = (projectId || 0) > 0;

          if (isFromProject)
          {
            if (configIDRoute === 0 || configIDRoute === 1)
            {
              configIDRoute = getConfigItemDefault(filteredMenuData);
            }
          }
          else
          {
            if (!configIDRoute || configIDRoute === 1)
            {
              configIDRoute = getConfigItemDefault(filteredMenuData);
            }
          }

          if (configIDRoute)
          {
            defaultItem = filteredMenuData.find(
              (i: any) => (i.ConfigValueId || i.ConfigValueID) === configIDRoute,
            );
          }

          if (!defaultItem)
          {
            defaultItem =
              filteredMenuData.find((i: any) => i.ConfigValueId === initialConfigValueId) ||
              filteredMenuData[0];
            configIDRoute =
              defaultItem?.ConfigValueId || defaultItem?.ConfigValueID || initialConfigValueId;
          }

          prevItemTypeRef.current = defaultItem;
          // Pre-set refs before setSelectedItemType so the CF effect guard sees them
          // when it fires during the async pause of getEmptyCustomFields below.
          prevConfigValueIdRef.current = defaultItem.ConfigValueId ?? defaultItem.ConfigValueID;
          prevCustomFieldProjectIdRef.current = projectId ?? null;
          setSelectedItemType(defaultItem);
        }

        // 2. Fetch empty custom fields — response includes layer metadata, eliminating
        //    separate GetLayersByConfigValueId + GetLayersForCardBy calls.
        if (defaultItem)
        {
          const cfgId = defaultItem.ConfigValueId ?? defaultItem.ConfigValueID;
          const [fields, layerData] = await Promise.all([
            dashboardGroupsService
              .getEmptyCustomFields(cfgId, projectId || undefined)
              .catch(() => []),
            dashboardGroupsService
              .getLayersByConfigValue(cfgId, projectId || undefined)
              .catch(() => []),
          ]);

          if (cancelled) return;

          const result = Array.isArray(fields) ? fields : ((fields as any)?.result ?? []);
          const emptyFields = Array.isArray(result) ? result : [];

          const visible = getVisibleLayers(
            (layerData || []).map((l: any) => ({
              LayerId: l.LayerId,
              Name: l.Name || l.LayerName || '',
              LayerName: l.Name || l.LayerName || '',
              Color: l.Color || l.LayerColor || '',
              LayerColor: l.Color || l.LayerColor || '',
              TextColor: l.TextColor || false,
              IsDefault: l.IsDefault || false,
              isConfigLayer: true,
              PriorityInConfigValue: l.PriorityInConfigValue || 0,
            })),
          );

          const storedLayerId = parseInt(localStorage.getItem('C2_layerid') ?? '0', 10);
          const fromStored =
            storedLayerId > 0 ? visible.find((l: any) => l.LayerId === storedLayerId) : undefined;
          const defaultLayer =
            fromStored ?? visible.find((l: any) => l.IsDefault) ?? visible[0] ?? null;

          setEmptyCustomFields(emptyFields);
          setPreloadedLayers(visible);
          setCustomFieldsReady(true);
          if (defaultLayer)
          {
            setSelectedLayer(defaultLayer);
            setSelectedLayers([defaultLayer]);
          }
        }

        fetchDataRunningRef.current = false;
        setLoading(false);
        setTimeout(() => inputRef.current?.focus(), 100);
      }
      catch (err)
      {
        console.error('Error fetching inline task data:', err);
        fetchDataRunningRef.current = false;
        setLoading(false);
      }
    };

    fetchData();

    const handleLayerChange = (e: any) =>
    {
      const newLayerId = e.detail?.layerId;
      if (newLayerId)
      {
        const allLayersStr = sessionStorage.getItem('C2_AllLayers');
        if (allLayersStr)
        {
          try
          {
            const allLayers = JSON.parse(allLayersStr);
            const newLayer = allLayers.find((l: any) => l.LayerId === newLayerId);
            if (newLayer)
            {
              setSelectedLayer(newLayer);
              setSelectedLayers([newLayer]);
            }
          }
          catch (err)
          {
            console.error('Error updating layer from event', err);
          }
        }
      }
    };

    window.addEventListener('layerChanged', handleLayerChange);

    return () =>
    {
      cancelled = true;
      window.removeEventListener('layerChanged', handleLayerChange);
    };
  }, [projectId, initialConfigValueId]);

  const prevItemTypeRef = useRef<any>(null);

  const handleKeyDown = (e: React.KeyboardEvent) =>
  {
    if (e.key === 'Enter')
    {
      e.preventDefault();
      handleSubmit();
    }
    else if (e.key === 'Escape')
    {
      onCancel();
    }
  };

  const isProjectType =
    (selectedItemType?.ConfigValueId || selectedItemType?.ConfigValueID) === 9;

  // Every card needs at least one layer (same rule as EditCard)
  const hasLayers = selectedLayers.length > 0 || !!selectedLayer;

  const handleSubmit = async () =>
  {
    if (!taskName.trim()) return;
    if (!hasLayers)
    {
      setShowDetails(true);
      return;
    }
    if (isProjectType && (selectedSecurityGroups.length === 0 || selectedLayers.length === 0))
    {
      return;
    }

    setSubmitting(true);

    try
    {
      // 1. Create Conversation
      const configValueId =
        selectedItemType?.ConfigValueId || selectedItemType?.ConfigValueID || initialConfigValueId;
      const currentConv = conversationRef.current;

      // Map CustomFields from ref or conversation
      let fieldsToSend: any[] = [];
      if (showDetails)
      {
        if (customFieldsDataRef.current && customFieldsDataRef.current.length > 0)
        {
          // customFieldsDataRef.current is LayerCustomFields[], we need to flatten it
          const allFields: any[] = [];
          customFieldsDataRef.current.forEach((layer: any) =>
          {
            if (layer.CustomFields && Array.isArray(layer.CustomFields))
            {
              allFields.push(...layer.CustomFields);
            }
          });

          fieldsToSend = allFields.map((f: any) => ({
            CustomFieldId: f.CustomFieldId,
            Value: f.Value,
            CoreTaskID: 0,
            FieldTypeId: f.FieldTypeId,
          }));
        }
        else if (currentConv?.CustomFields)
        {
          fieldsToSend = currentConv.CustomFields;
        }
      }

      const payload = {
        ConversationId: 0,
        Attachments: [],
        IsFromProject: false,
        ConfigValueId: configValueId,
        ConversationLayers: (selectedLayers.length > 0 ? selectedLayers : selectedLayer ? [selectedLayer] : []).map(
          (layer: any) => ({
            LayerId: layer.LayerId,
            Name: layer.Name || layer.LayerName,
            Color: layer.Color || layer.LayerColor,
            TextColor: layer.TextColor,
            IsEnabled: false,
            IsDefault: false,
          }),
        ),
        ConversationGroups: currentConv?.ConversationGroups || [],
        ConversationSecurityGroups: currentConv?.ConversationSecurityGroups || [],
        CustomFields: fieldsToSend,
        Title: taskName,
        Body: currentConv?.Body || '',
        ProjectId: currentProjectId || null,
        ProjectName: currentProjectId ? currentProjectName : '',
        RelatedConversationId: 0,
        RelatedReplyId: 0,
        IsRedFlag: currentConv?.IsRedFlag || false,
        IsFavorite: false,
        SideMenuText: '',
        Icon: '',
        Tags: currentConv?.Tags || [],
        Hashtags: [],
        IsEnable: true,
        IsAdmin: true,
        IsAdminProject: false,
        OwnedById: currentConv?.OwnedById || 0,
        DashboardId: dashboardId,
        DashboardGroupId: groupId,
        Metadata: {},
        SocketConnectionId: socketConnectionId,
      };

      const response = await dashboardGroupsService.createConversation(payload);

      if (response && response.Conversation)
      {
        const newConv = response.Conversation;

        // 2. Insert into Dashboard Group
        await dashboardGroupsService.insertDashboardGroupConversation({
          DashboardId: dashboardId,
          DashboardGroupId: groupId,
          ConversationId: newConv.ConversationId,
          ParentConversationId: 0,
          PreviousParentId: configValueId, // Using config ID as PreviousParentId based on example
          socketConnectionId: socketConnectionId,
        });

        // 3. Update Percent (Stats)
        await dashboardGroupsService.updatePercentDashboardGroupConversations({
          dashboardGroupConvs: [{ DashboardGroupId: groupId }],
          DashboardGroupIdDefault: 0,
        });

        // Success! Clear name and refocus so user can add another card immediately.
        setTaskName('');
        setSubmitting(false);
        if (onAdd)
        {
          // Pass the new conversation AND explicitly set the DashboardGroupId/DashboardId
          // because the create API response might have them as null
          onAdd({
            ...newConv,
            DashboardGroupId: groupId,
            DashboardId: dashboardId,
          });
        }
        setTimeout(() => inputRef.current?.focus(), 50);
      }
      else
      {
        console.error('Failed to create conversation', response);
        setSubmitting(false);
      }
    }
    catch (error)
    {
      console.error('Error in handleSubmit', error);
      setSubmitting(false);
    }
  };

  // Refs to break dependency cycles in callbacks
  const currentProjectIdRef = useRef(currentProjectId);
  // handleCustomFieldsUpdate has empty deps, so read the flag through a ref
  const isProjectScopedRef = useRef(isProjectScoped);
  isProjectScopedRef.current = isProjectScoped;
  const selectedLayerRef = useRef(selectedLayer);
  const selectedSecurityGroupsRef = useRef(selectedSecurityGroups);

  useEffect(() =>
  {
    currentProjectIdRef.current = currentProjectId;
  }, [currentProjectId]);
  useEffect(() =>
  {
    selectedLayerRef.current = selectedLayer;
  }, [selectedLayer]);
  useEffect(() =>
  {
    selectedSecurityGroupsRef.current = selectedSecurityGroups;
  }, [selectedSecurityGroups]);

  const handleCustomFieldsUpdate = useCallback((fields: any[], conversation: any) =>
  {
    customFieldsDataRef.current = fields;
    conversationRef.current = conversation;

    // Update local state if project ID changed in the conversation object (e.g. from Project selector)
    if (conversation)
    {
      // Check changes first
      const incomingProjectId = conversation.ProjectId || null;
      const projectIdChanged =
        !isProjectScopedRef.current &&
        conversation.ProjectId !== undefined &&
        incomingProjectId !== currentProjectIdRef.current;

      // Check layer changes
      let layerChanged = false;
      let newLayer = null;
      if (conversation.ConversationLayers)
      {
        if (conversation.ConversationLayers.length > 0)
        {
          newLayer = conversation.ConversationLayers[0];
        }
        if (newLayer?.LayerId !== selectedLayerRef.current?.LayerId)
        {
          layerChanged = true;
        }
      }

      // Check security groups changes
      const incomingGroups = conversation.ConversationGroups || [];
      const groupsChanged =
        incomingGroups.length !== selectedSecurityGroupsRef.current.length ||
        incomingGroups.some(
          (g: any, i: number) => g.GroupId !== selectedSecurityGroupsRef.current[i]?.GroupId,
        );

      if (projectIdChanged || layerChanged || groupsChanged)
      {
        // Wrap updates in setTimeout to avoid "Cannot update component while rendering" error
        setTimeout(() =>
        {
          if (projectIdChanged)
          {
            setCurrentProjectId(incomingProjectId);
            if (conversation.ProjectName)
            {
              setCurrentProjectName(conversation.ProjectName);
            }
            else
            {
              setCurrentProjectName('');
            }
          }
          if (layerChanged)
          {
            setSelectedLayer(newLayer);
            setSelectedLayers(
              conversation.ConversationLayers.length > 0
                ? conversation.ConversationLayers
                : newLayer ? [newLayer] : [],
            );
          }
          if (groupsChanged)
          {
            setSelectedSecurityGroups(incomingGroups);
          }
        }, 0);
      }
    }
  }, []); // Stable callback

  const handleOnChangeLayers = useCallback(
    (conversation: any) =>
    {
      if (conversation?.ConversationLayers)
      {
        const layers = conversation.ConversationLayers as any[];
        const newLayer = layers.length > 0 ? layers[0] : null;
        setSelectedLayer(newLayer);
        setSelectedLayers(layers);
      }
      handleCustomFieldsUpdate(customFieldsDataRef.current, conversation);
    },
    [handleCustomFieldsUpdate],
  );

  const handleAddLayerToProject = useCallback(
    async (
      _e: any,
      layer: any,
      functions?: { onUpdateLayerToCard?: (layerId: number) => void },
    ) =>
    {
      let projectConversationId = layer?.ProjectConversationId;

      if (!projectConversationId && currentProjectId)
      {
        try
        {
          const proj = await apiService.get<any>(
            `Project/GetProject?projectId=${currentProjectId}`,
          );
          projectConversationId = proj?.ConversationId;
        }
        catch (_) { /* best-effort */ }
      }

      if (!projectConversationId) return;

      const layerName = layer.Name || layer.LayerName || '';
      const layerColor = layer.Color || layer.LayerColor || '';

      try
      {
        const projectConv = await apiService.post<any>(
          `conversation/GetConversationById?conversationId=${projectConversationId}`,
          null,
        );
        if (projectConv && projectConv.ConfigValueId !== 15)
        {
          let fetchedGroups: any[] = [];
          try
          {
            const result = await apiService.get<any[]>(
              `group/GetGroupsByProject?projectId=${projectConv.ProjectId}`,
            );
            if (Array.isArray(result)) fetchedGroups = result;
          }
          catch (_) { /* best-effort */ }

          await apiService.post<any>('conversation/updateconversation', {
            ...projectConv,
            CustomFields: [],
            ConversationGroups: fetchedGroups.length > 0 ? fetchedGroups : projectConv.ConversationGroups,
            ConversationLayers: [
              ...(projectConv.ConversationLayers || []),
              {
                ConversationId: projectConv.ConversationId,
                LayerId: layer.LayerId,
                LayerName: layerName,
                LayerColor: layerColor,
                isForAdd: true,
                IsActive: 1,
              },
            ],
            SocketConnectionId: socketConnectionId,
          });
        }
      }
      catch (err)
      {
        console.error('Failed to add layer to project:', err);
      }

      functions?.onUpdateLayerToCard?.(layer.LayerId);
    },
    [currentProjectId],
  );

  const customFieldsConfig = React.useMemo(
    () => ({
      apiUrl: api.url,
      authToken: api.token,
      canEdit: true,
      isNew: true,
      showHeader: true,
      callApi: false,
      disableAutoSave: true,
      preloadedLayers,
      onChangedProject: (_e: any, conversation: any) =>
        handleCustomFieldsUpdate(customFieldsDataRef.current, conversation),
      onChangeLayers: handleOnChangeLayers,
      onChangedSecurityGroups: (_e: any, conversation: any) =>
        handleCustomFieldsUpdate(customFieldsDataRef.current, conversation),
      onChangedGroupLayers: (_e: any, conversation: any) =>
        handleCustomFieldsUpdate(customFieldsDataRef.current, conversation),
      UpdateCustomFields: (fields: any[], conversation: any) =>
        handleCustomFieldsUpdate(fields, conversation),
      UpdateDetailsConversationReact: (fields: any[], conversation: any) =>
        handleCustomFieldsUpdate(fields, conversation),
      addLayerToProject: handleAddLayerToProject,
    }),
    [api.url, api.token, handleCustomFieldsUpdate, handleOnChangeLayers, handleAddLayerToProject],
  );

  const customFieldsItem = React.useMemo(
    () => ({
      ConversationId: 0,
      ProjectId: currentProjectId ?? null,
      ProjectName: currentProjectName,
      ConfigValueId: selectedItemType
        ? selectedItemType.ConfigValueId || selectedItemType.ConfigValueID
        : 0,
      selectedConfigValueId: selectedItemType
        ? selectedItemType.ConfigValueId || selectedItemType.ConfigValueID
        : 0,
      LayerCustomFields: emptyCustomFields,
      ConversationLayers: (selectedLayers.length > 0 ? selectedLayers : selectedLayer ? [selectedLayer] : []).map(
        (layer: any) => ({
          ConversationId: 0,
          ConversationLayerId: 0,
          IsActive: false,
          IsDefault: layer.IsDefault || false,
          LayerColor: layer.Color || layer.LayerColor || '',
          LayerId: layer.LayerId,
          LayerName: layer.Name || layer.LayerName || '',
          PriorityInConfigValue: layer.PriorityInConfigValue || 0,
          WasEdited: null,
        }),
      ),
      ConversationGroups: [],
      dashboardGroupId: groupId,
      isAddLayertoCard: false,
      isFromConvertTo: false,
    }),
    [
      currentProjectId,
      currentProjectName,
      selectedItemType,
      emptyCustomFields,
      groupId,
      selectedLayer,
      selectedLayers,
    ],
  );

  if (loading) return <div className="p-3 text-gray-500 text-sm">Loading...</div>;

  return (
    <div className="add-inline-task-container relative group">
      <div className="flex items-center w-full py-0.5 ">
        {/* Layer dots — up to 3, matching other card views */}
        <div className="corgee_com_title-card-header-layers flex-shrink-0">
          {(selectedLayers.length > 0 ? selectedLayers : selectedLayer ? [selectedLayer] : []).map(
            (layer, idx) => (
              <span
                key={layer.LayerId ?? idx}
                className="corgee_com_title-card-layer"
                style={{ backgroundColor: layer.LayerColor || layer.Color || '#ccc' }}
              />
            ),
          )}
        </div>

        <div className='gap-2 flex items-center flex-1 min-w-0 pr-1'>
          {/* Icon Picker Trigger */}
          <div className="relative flex-shrink-0">
            <div
              className={`flex items-center justify-center rounded transition-colors px-1 gap-1 ${menuItems.length > 1 ? 'cursor-pointer hover:bg-gray-100' : 'cursor-default'}`}
              onClick={() =>
              {
                if (menuItems.length > 1) setShowIconPicker(!showIconPicker);
              }}
            >
              {selectedItemType &&
              selectedItemType.Name !== 'Task' &&
              selectedItemType.Name !== 'Users' ? (
                  <span className={`${selectedItemType.Icon} text-lg text-gray-600`}></span>
                ) : (
                  <>
                    <span
                      className={`identifier-icon-key ${selectedItemType?.Icon || 'icons-menu-tasks'}`}
                    ></span>
                  </>
                )}
            </div>
            {showIconPicker && (
              <div className="absolute top-8 left-0 z-50">
                <IconPicker
                  items={menuItems}
                  onSelect={item =>
                  {
                    setSelectedItemType(item);
                    setShowIconPicker(false);
                    setCustomFieldsKey(prev => prev + 1);
                  }}
                  onClose={() => setShowIconPicker(false)}
                />
              </div>
            )}
          </div>

          {/* Input Area */}
          <div className="add-inline-task-name flex-1 relative flex items-center border border-gray-200 rounded-sm bg-white overflow-hidden focus-within:ring-1 focus-within:ring-blue-300 transition-shadow h-6">
            <input
              ref={inputRef}
              type="text"
              className="flex-1 min-w-0 w-full text-sm px-2 py-1 outline-none text-gray-700 placeholder-gray-400 h-full"
              placeholder="Type name here"
              value={taskName}
              onChange={e => setTaskName(e.target.value)}
              onKeyDown={handleKeyDown}
              autoFocus
            />
          </div>

          {/* Add Button */}
          {taskName.trim().length > 0 && hasLayers &&
            (!isProjectType || (selectedSecurityGroups.length > 0 && selectedLayers.length > 0)) && (
            <button
              className="flex-shrink-0 text-white text-xs font-medium px-2 h-6 hover:opacity-90 transition-colors rounded-sm flex items-center justify-center tracking-wide disabled:opacity-50 disabled:cursor-not-allowed"
              style={{
                backgroundColor: selectedLayer?.Color || selectedLayer?.LayerColor || '#62aacb',
              }}
              onClick={handleSubmit}
              disabled={submitting}
            >
              Add
            </button>
          )}

          {/* Show Details Trigger (Icon) */}
          <button
            className={`flex-shrink-0 w-6 h-6 flex items-center justify-center rounded text-gray-400 hover:text-gray-600 transition-colors ${showDetails ? 'text-blue-500' : ''}`}
            onClick={() => setShowDetails(!showDetails)}
            title="Show Details"
          >
            <span className="material-icons text-lg">list</span>
          </button>
        </div>
      </div>

      {/* Kept mounted while collapsed so project defaults resolve in the background */}
      {selectedItemType && (
        <div
          className={`corgee_com_title-card-details-panel max-h-96 overflow-y-auto overflow-x-hidden w-full ${showDetails ? '' : 'hidden'}`}
        >
          {customFieldsReady && emptyCustomFields.length > 0 ? (
            <div className="add-inline-task-fields">
              <CustomFields
                key={`cf-${selectedItemType.ConfigValueId || selectedItemType.ConfigValueID}-${customFieldsKey}`}
                config={customFieldsConfig}
                UpdateCustomFields={(fields: any[], conversation: any, _updatedField: any) =>
                  handleCustomFieldsUpdate(fields, conversation)
                }
                configValueId={selectedItemType.ConfigValueId || selectedItemType.ConfigValueID}
                projectId={currentProjectId ?? null}
                item={customFieldsItem}
                isInlineTask={true}
                lockProject={isProjectScoped}
              />
            </div>
          ) : customFieldsReady ? (
            <div className="text-center py-4 text-gray-400 text-sm">
              No custom fields available.
            </div>
          ) : (
            <div className="text-center py-4 text-gray-400 text-sm">Loading fields...</div>
          )}
        </div>
      )}
    </div>
  );
};
